import { useMutation } from '@tanstack/react-query'
import { useCallback, useState } from 'react'

import { wishlistKeys } from './queryKeys'
import { wishlistApi } from '../../lib'

import type { GraphQLResponse } from '../../types'

interface UseWishlistMutationsProps {
  wishlistId: string | null
  refetch: () => void
  getWishlistItemId: (sku: string) => string | null
}

interface UseWishlistMutationsResult {
  processingWishlist: string
  errorMessage: string | null
  itemsCountOverride: number | null
  addToWishlist: (sku: string) => void
  removeFromWishlist: (sku: string) => void
}

function extractErrorMessage(response: GraphQLResponse, operation: 'add' | 'remove'): string | null {
  const operationData = operation === 'add'
    ? response.data?.addProductsToWishlist
    : response.data?.removeProductsFromWishlist

  if (operationData?.user_errors?.length) {
    return operationData.user_errors
      .map(error => error.message)
      .join(', ')
  }

  if (response.errors?.length) {
    return response.errors[0].debugMessage || response.errors[0].message || 'An error occurred'
  }

  return null
}

function dispatchWishlistUpdated(): void {
  document.dispatchEvent(new CustomEvent('wishlistUpdated', {}))
}

export function useWishlistMutations({
  wishlistId,
  refetch,
  getWishlistItemId,
}: UseWishlistMutationsProps): UseWishlistMutationsResult {
  const [processingWishlist, setProcessingWishlist] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [itemsCountOverride, setItemsCountOverride] = useState<number | null>(null)

  const addMutation = useMutation({
    mutationKey: wishlistKeys.mutations.add,
    mutationFn: ({ currentWishlistId, sku }: { currentWishlistId: string; sku: string }) =>
      wishlistApi.addProducts(currentWishlistId, [{ sku, quantity: 1 }]),
    onSuccess: (response: GraphQLResponse) => {
      setProcessingWishlist('')

      const errorMsg = extractErrorMessage(response, 'add')
      setErrorMessage(errorMsg)

      if (!errorMsg) {
        const newCount = response.data?.addProductsToWishlist?.wishlist?.items_count
        if (newCount !== undefined) {
          setItemsCountOverride(newCount)
        }
        void refetch()
        dispatchWishlistUpdated()
      }
    },
    onError: (error: unknown) => {
      // eslint-disable-next-line no-console -- Error logging is intentional
      console.error('Failed to add to wishlist:', error)
      setProcessingWishlist('')
      setErrorMessage('Failed to add product to wishlist. Please try again.')
    },
  })

  const removeMutation = useMutation({
    mutationKey: wishlistKeys.mutations.remove,
    mutationFn: ({ currentWishlistId, wishlistItemId }: { currentWishlistId: string; wishlistItemId: string }) =>
      wishlistApi.removeProducts(currentWishlistId, [wishlistItemId]),
    onSuccess: (response: GraphQLResponse) => {
      setProcessingWishlist('')

      const errorMsg = extractErrorMessage(response, 'remove')
      setErrorMessage(errorMsg)

      if (!errorMsg) {
        const newCount = response.data?.removeProductsFromWishlist?.wishlist?.items_count
        if (newCount !== undefined) {
          setItemsCountOverride(newCount)
        }
        void refetch()
        dispatchWishlistUpdated()
      }
    },
    onError: (error: unknown) => {
      // eslint-disable-next-line no-console -- Error logging is intentional
      console.error('Failed to remove from wishlist:', error)
      setProcessingWishlist('')
      setErrorMessage('Failed to remove product from wishlist. Please try again.')
    },
  })

  const addToWishlist = useCallback(
    (sku: string) => {
      if (!wishlistId) {
        setErrorMessage('Wishlist not available. Please try again.')
        return
      }

      setProcessingWishlist(sku)
      addMutation.mutate({ currentWishlistId: wishlistId, sku })
    },
    [wishlistId, addMutation]
  )

  const removeFromWishlist = useCallback(
    (sku: string) => {
      if (!wishlistId) {
        return
      }

      const wishlistItemId = getWishlistItemId(sku)
      if (!wishlistItemId) {
        return
      }

      setProcessingWishlist(sku)
      removeMutation.mutate({ currentWishlistId: wishlistId, wishlistItemId })
    },
    [wishlistId, getWishlistItemId, removeMutation]
  )

  return {
    processingWishlist,
    errorMessage,
    itemsCountOverride,
    addToWishlist,
    removeFromWishlist,
  }
}
